import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import useIntl from '@afosto/utils-react/intl/useIntl';
import { useFormContext } from '../Form';
import { useSnackbar } from 'notistack';
import isEqual from 'lodash.isequal';
import Button from '@material-ui/core/Button';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CardHeader from '@material-ui/core/CardHeader';
import FormDrawer from '../FormDrawer';
import OrganisationForm from '../OrganisationForm';
import OrganisationSearch from '../OrganisationSearch';
import addOrganisationToContact from '@afosto/utils-react/contact/addOrganisationToContact';
import getContactDisplay from '@afosto/utils/getContactDisplay';
import { organisations as organisationsApi } from '@afosto/contacts/api/Organisations';
import translations from './translations';

const OrganisationFormSection = ({
  autoFillFromContact,
  contact,
  name,
  onChange,
  onUpdateContact,
  requiresContact,
  value,
}) => {
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const [showCreateOrganisation, setShowCreateOrganisation] = useState(false);
  const formContext = useFormContext();
  const { setValue, watch } = formContext || {};
  const setValueRef = useRef(setValue);
  const contactRef = useRef(contact);
  const previousContactRef = useRef(contact);
  const contactIsDifferent = !isEqual(contact?.id, previousContactRef.current?.id);
  const inputValue = value ?? (watch && watch(name));

  useEffect(() => {
    setValueRef.current = setValue;
    contactRef.current = contact;
  });

  const handleChangeOrganisation = (ev, newValue) => {
    if (formContext && !onChange) {
      setValue(name, newValue);
      return;
    }

    if (onChange && typeof onChange === 'function') {
      onChange(ev, newValue);
    }
  };

  const handleUpdateContact = (ev, newValue) => {
    if (onUpdateContact && typeof onUpdateContact === 'function') {
      onUpdateContact(ev, newValue);
    }
  };

  /**
   * Handle create a new organisation for the contact.
   * @param {string} name
   * @param {string} cocNumber
   * @param {String} accountManager
   * @param {number} paymentTerm
   * @param {array} iban
   * @param {boolean} isPrimary
   * @param {string} administrationEmail
   * @param {boolean} receiveInvoiceInEmail
   * @param {boolean} receiveInvoiceInMail
   * @param {boolean} receiveInvoiceAtDelivery
   * @param {string} logo
   * @param {array} tags
   * @returns {Promise<void>}
   */
  const handleCreateOrganisation = async ({
    name: organisationName,
    cocNumber,
    accountManager,
    paymentTerm,
    iban,
    isPrimary,
    receiveInvoiceInEmail,
    receiveInvoiceInMail,
    receiveInvoiceAtDelivery,
    administrationEmail,
    logo,
    tags,
  }) => {
    try {
      const response = await organisationsApi.createOrganisation({
        name: organisationName,
        coc_number: cocNumber,
        account_manager: accountManager?.id ?? null,
        file_id: logo || null,
        tags: tags ?? [],
        administration: {
          email: administrationEmail,
        },
        payment_settings: {
          post_payment: {
            due_after: parseInt(paymentTerm, 10),
            iban: iban ?? [],
          },
          invoice: {
            is_expected_through_post_mail: receiveInvoiceInMail,
            is_expected_through_email: receiveInvoiceInEmail,
            is_expected_at_delivery: receiveInvoiceAtDelivery,
          },
        },
      });
      const { data: createdOrganisation } = response;

      handleChangeOrganisation(
        {},
        {
          ...createdOrganisation,
          label: createdOrganisation.name,
          value: createdOrganisation.id,
        },
      );

      if (requiresContact) {
        const updatedContact = await addOrganisationToContact(
          contact,
          createdOrganisation,
          isPrimary,
        );

        handleUpdateContact(
          {},
          {
            ...updatedContact,
            label: getContactDisplay(updatedContact),
            value: updatedContact?.id,
          },
        );
      }

      enqueueSnackbar(intl.formatMessage(translations.feedback.organisationCreated), {
        variant: 'success',
      });

      setShowCreateOrganisation(false);
    } catch (err) {
      enqueueSnackbar(intl.formatMessage(translations.feedback.organisationNotCreated), {
        variant: 'error',
      });
    }
  };

  useEffect(() => {
    const { current: currentContact } = contactRef;
    if (autoFillFromContact && requiresContact && contactIsDifferent) {
      const filteredSecondaryOrganisations = currentContact?.organisations?.secondary;

      if (
        currentContact?.organisations?.primary?.id &&
        !currentContact?.organisations?.primary?.deleted_at
      ) {
        const formattedOrganisation = {
          ...currentContact?.organisations?.primary,
          label: currentContact?.organisations?.primary?.name,
          value: currentContact?.organisations?.primary?.id,
        };
        setValueRef.current(name, formattedOrganisation);
      } else if (
        filteredSecondaryOrganisations?.length > 0 &&
        filteredSecondaryOrganisations[0]?.id &&
        !filteredSecondaryOrganisations[0]?.deleted_at
      ) {
        const firstSecondaryOrganisation = filteredSecondaryOrganisations[0];
        const formattedOrganisation = {
          ...firstSecondaryOrganisation,
          label: firstSecondaryOrganisation?.name,
          value: firstSecondaryOrganisation?.id,
        };
        setValueRef.current(name, formattedOrganisation);
      } else {
        setValueRef.current(name, null);
      }
    }
  }, [contactIsDifferent, requiresContact, autoFillFromContact, name]);

  useEffect(() => {
    previousContactRef.current = contact;
  });

  return (
    <>
      <Card style={{ marginBottom: 8 }}>
        <CardHeader
          title={intl.formatMessage(translations.page.organisationFormSection.title)}
          titleTypographyProps={{ variant: 'h6' }}
          action={
            <Button
              color="primary"
              variant="outlined"
              disabled={requiresContact && !contact}
              onClick={() => setShowCreateOrganisation(true)}
            >
              {intl.formatMessage(translations.page.organisationFormSection.button.create.label)}
            </Button>
          }
        />
        <CardContent>
          <OrganisationSearch
            contact={contact}
            disabled={requiresContact && !contact}
            label={intl.formatMessage(translations.page.organisationFormSection.title)}
            name={name}
            onChange={handleChangeOrganisation}
            value={inputValue}
          />
        </CardContent>
      </Card>
      <FormDrawer
        isOpen={showCreateOrganisation}
        onClose={() => setShowCreateOrganisation(false)}
        drawerWidth="400px"
        title={intl.formatMessage(translations.page.createOrganisation.title)}
      >
        <OrganisationForm
          onCancel={() => setShowCreateOrganisation(false)}
          onSubmit={handleCreateOrganisation}
          showSetAsPrimary={!!contact?.organisations?.primary}
        />
      </FormDrawer>
    </>
  );
};

OrganisationFormSection.propTypes = {
  autoFillFromContact: PropTypes.bool,
  contact: PropTypes.object,
  name: PropTypes.string,
  onChange: PropTypes.func,
  onUpdateContact: PropTypes.func,
  requiresContact: PropTypes.bool,
  value: PropTypes.object,
};

OrganisationFormSection.defaultProps = {
  autoFillFromContact: false,
  contact: null,
  name: 'organisation',
  onChange: undefined,
  onUpdateContact: undefined,
  requiresContact: true,
  value: undefined,
};

export default OrganisationFormSection;
